"use client";
import { motion } from "framer-motion";
import { ArrowUpRight, Send, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { BRAND, MEDIA } from "../../lib/brand";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Pill */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col items-center text-center"
        >
          <motion.div variants={fadeUp}>
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass text-xs uppercase tracking-[0.18em] text-neutral-300"
              data-testid="hero-pill"
            >
              <Sparkles className="h-3.5 w-3.5 text-[#00E5FF]" />
              Premium Telegram Bot Studio
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={fadeUp}
            className="font-display mt-7 text-5xl md:text-7xl lg:text-[88px] leading-[1.02] font-light tracking-tighter max-w-5xl"
            data-testid="hero-heading"
          >
            Build the next wave of{" "}
            <span className="gradient-text font-medium">Telegram automation</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-6 text-base md:text-lg text-neutral-400 max-w-2xl"
            data-testid="hero-subtitle"
          >
            {BRAND.name} crafts advanced Telegram bots & automation tools that power
            communities, games and workflows — engineered for scale, speed, and
            delight.
          </motion.p>

          {/* CTAs */}
          <motion.div variants={fadeUp} className="mt-9 flex flex-col sm:flex-row items-center gap-3">
            <Button
              asChild
              className="bg-[#007AFF] hover:bg-[#3B82F6] text-white rounded-full h-12 px-6 text-sm font-medium glow-blue group"
              data-testid="hero-cta-launch"
            >
              <a href={BRAND.flagshipBot.link} target="_blank" rel="noreferrer">
                <Send className="h-4 w-4 mr-2 group-hover:translate-x-0.5 transition-transform" />
                Launch Word Grid
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full h-12 px-6 text-sm bg-transparent border-white/15 text-white hover:bg-white/5 hover:border-white/30"
              data-testid="hero-cta-contact"
            >
              <a href="#contact">
                Contact Owner
                <ArrowUpRight className="h-4 w-4 ml-2" />
              </a>
            </Button>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs uppercase tracking-[0.2em] text-neutral-500"
            data-testid="hero-trust"
          >
            <span>Trusted by 7,000+ groups</span>
            <span className="text-neutral-700">·</span>
            <span>115,000+ users</span>
            <span className="text-neutral-700">·</span>
            <span>99.98% uptime</span>
          </motion.div>
        </motion.div>

        {/* Dashboard visual */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-16 md:mt-24"
        >
          <div className="relative mx-auto max-w-5xl">
            {/* Glow */}
            <div className="absolute -inset-10 -z-10 rounded-[40px] bg-gradient-to-tr from-[#007AFF]/30 via-transparent to-[#00E5FF]/20 blur-3xl" />
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative rounded-[28px] border border-white/10 overflow-hidden glow-soft"
            >
              <img
                src={MEDIA.hero_dashboard}
                alt="Axion Bots Dashboard Preview"
                className="w-full h-auto block"
                data-testid="hero-dashboard-image"
              />
              {/* Bottom fade */}
              <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent pointer-events-none" />
            </motion.div>

            {/* Floating cards */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="hidden md:flex absolute -left-10 top-20 glass rounded-2xl px-4 py-3 items-center gap-3"
              data-testid="hero-floating-card-1"
            >
              <div className="h-9 w-9 rounded-xl bg-[#007AFF]/20 grid place-items-center border border-[#007AFF]/30">
                <Send className="h-4 w-4 text-[#00E5FF]" />
              </div>
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wider">Active</p>
                <p className="text-sm font-medium">115,000+ users</p>
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="hidden md:flex absolute -right-8 bottom-24 glass rounded-2xl px-4 py-3 items-center gap-3"
              data-testid="hero-floating-card-2"
            >
              <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse-glow" />
              <p className="text-sm font-medium">All systems operational</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
